import React, { useEffect, useMemo, useState } from 'react';
import { Chip, CircularProgress, Grid, makeStyles, Theme } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { useHistory } from 'react-router';
import { FilterList } from '@material-ui/icons';
import Button from '../../../components/Button';
import InvestorOnboard from '../../investor/InvestorOnboard';
import { FilterOption, LoadingStatus, Role, UserNetwork } from '../../../types/types';
import { useAlert, useConnector, useUser } from '../../../contexts';
import NetworkListItem from '../../../components/NetworkListItem';
import { StyledText } from '../../../components/StyledText';
import ConnectorGuide from '../../../components/PublishingGuide/ConnectorGuide';
import NetworkFilterModal from '../../../components/NetworkFilterModal';
import { NetworkPagePathState } from '../../../types/router';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexDirection: 'column'
  },
  heading: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '40px',
    marginBottom: '10px'
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'baseline'
  },
  investorsCount: {
    marginLeft: '16px',
    color: theme.palette.primary.main
  },
  investorsLabel: {
    marginLeft: '8px',
    color: theme.palette.primary.main
  },
  filterContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%'
  },
  filterChipContainer: {
    display: 'flex',
    width: '100%',
    padding: theme.spacing(1, 0)
  },
  statusChip: {
    marginRight: theme.spacing(1)
  },
  filterButton: {
    width: 100
  }
}));
const ConnectorNetworkHome = () => {
  const styles = useStyles();
  const { userInfo } = useUser();
  const { userNetworkList,
    userNetworkTotalCount,
    currentNetworkPage,
    networkPageSize,
    networkLoadingStatus,
    canSendReferrals,
    shouldShowIncompleteProfileGuide,
    profile,
    networkFilterOptions,
    fetchProfile,
    updateCurrentNetworkPage } = useConnector();
  const { postNewAlert } = useAlert();
  const [onboardInvestor, setOnboardInvestor] = useState<boolean>(false);
  const history = useHistory();

  const { location } = history;
  const pathState = location.state;

  const [isFilterModalOpen, setFilterModalOpen] = useState<boolean>(false);
  const setFilterOptions = (opts: FilterOption[]) => {
    const castState = pathState as NetworkPagePathState | undefined;
    history.replace(history.location.pathname, { ...castState, networkFilterOptions: opts, page: 1 });
  };

  const handleFilterModalOpen = () => {
    setFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setFilterModalOpen(false);
  };

  const handleFilterModalSubmit = (filterOpts: FilterOption[]) => {
    setFilterOptions(filterOpts);
    setFilterModalOpen(false);
  };

  const handleFilterRemove = (v: FilterOption) => {
    const filterOpts = networkFilterOptions.filter((i) => i.key !== v.key);
    setFilterOptions(filterOpts);
  };

  const handleAddInvestorToNetwork = async (id?: number) => {
    setOnboardInvestor(false);
    console.log('handle add investor');
    if (id) {
      history.push(`/investor/${id}`, { from: window.location.pathname });
    } else {
      postNewAlert('Something went wrong. Investor was not added to your network.');
    }
  };

  const handleOpenProfile = async () => {
    history.push(`/connector/${profile.id}`);
  };

  useEffect(() => {
    if (userInfo && profile && userInfo.role === Role.Connector) {
      if (userInfo.profileId !== profile.id) {
        fetchProfile(userInfo.profileId);
      }
    }
    // fetchProfile never changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo, profile]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    updateCurrentNetworkPage(page);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentNetworkPage]);

  const networkListEmpty = userNetworkList.length === 0 && networkFilterOptions.length === 0;

  return (
    <Grid container className={styles.root}>
      <InvestorOnboard
        open={onboardInvestor}
        handleClose={() => setOnboardInvestor(false)}
        goToProfile={handleAddInvestorToNetwork}
      />
      <NetworkFilterModal
        open={isFilterModalOpen}
        filterOptions={networkFilterOptions}
        onClose={handleFilterModalClose}
        onSubmit={handleFilterModalSubmit}
      />
      <Grid item xs={12}>
        <div className={styles.heading}>
          <div className={styles.titleWrapper}>
            <StyledText variant='h1'>Network</StyledText>
            <StyledText variant='counter' className={styles.investorsCount}>{userNetworkTotalCount}</StyledText>
            <StyledText variant='subtitle1' className={styles.investorsLabel}>Investors</StyledText>
          </div>
          {canSendReferrals && <Button type='button' onClick={() => setOnboardInvestor(true)}>Add Investor</Button>}
        </div>
        <div className={styles.filterContainer} style={(!networkListEmpty && canSendReferrals) ? undefined : { display: 'none' }}>
          <div className={styles.filterButton}>
            <Button
              startIcon={<FilterList />}
              variant='text'
              color='secondary'
              type='button'
              onClick={handleFilterModalOpen}
            >
              Filter
            </Button>
          </div>
          <div className={styles.filterChipContainer}>
            {networkFilterOptions.map((c: FilterOption) => (
              <div className={styles.statusChip}>
                <Chip
                  label={c.label}
                  variant='outlined'
                  color='default'
                  onDelete={() => handleFilterRemove(c)}
                />
              </div>
            ))}
          </div>
        </div>
      </Grid>
      { shouldShowIncompleteProfileGuide
        && (
        <ConnectorGuide
          profile={profile}
          handleClick={handleOpenProfile}
        />
        )}
      { canSendReferrals
        && (
        <Grid item xs={12}>
          {networkLoadingStatus === LoadingStatus.Loading
            ? <CircularProgress />
            : userNetworkList.map((item: UserNetwork, idx: number) => (
              <NetworkListItem
                key={`network-item-${idx}`}
                userNetwork={item}
              />
            ))}
          { userNetworkTotalCount === 0 && networkFilterOptions.length > 0 && (
            <StyledText style={{ marginTop: '20px' }}>No investors matching criteria.</StyledText>
          )}
          {userNetworkTotalCount > 0
          && (
          <Pagination
            count={Math.ceil(userNetworkTotalCount / networkPageSize)}
            page={currentNetworkPage}
            color='primary'
            onChange={handlePageChange}
          />
          )}
        </Grid>
        )}
    </Grid>
  );
};

export default ConnectorNetworkHome;

